import { Location } from '@angular/common';
import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import {
  DEFAULT_PRODUCT_SLUG,
  PRODUCT_CONFIG,
  PRODUCT_SECTIONS,
  productByPath,
  type ProductSectionKey,
} from '../data/products.config';
import { canonicalPath, localePath } from '../utils/locale-path';
import { LanguageService } from './language.service';

@Injectable({ providedIn: 'root' })
export class ProductContextService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly language = inject(LanguageService);

  // Seeded from the browser, not from Router.url: the initial navigation is not
  // blocking, so Router.url is still '/' on the first paint of a hard load.
  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.location.path() || '/' },
  );

  // The product is a fact about the path, not about the language it is read in,
  // so the language prefix comes off before the match: `/fr/catalog` is Catalog.
  readonly slug = computed(
    () => productByPath(canonicalPath(this.url()).split(/[?#]/)[0])?.slug ?? null,
  );

  /** The product in view, or null outside a product area. */
  readonly current = computed(
    () => PRODUCT_CONFIG.find((product) => product.slug === this.slug()) ?? null,
  );

  /** The product a product-scoped link resolves to: the one in view, else the default. */
  readonly linkTarget = computed(() =>
    PRODUCT_CONFIG.find((product) => product.slug === (this.slug() ?? DEFAULT_PRODUCT_SLUG)),
  );

  /**
   * The hue of the product in view — null outside a product area, where site
   * chrome carries Avyna's own brand colour rather than any one product's.
   * Deliberately keyed on `slug`, not `linkTarget`: the default product is the
   * right destination for a link, never the right colour for the whole site.
   */
  readonly accent = computed(() => this.current()?.accent ?? null);

  /**
   * A section of the product in view, or of the site default outside a product
   * area. Undefined when that product has no such page.
   */
  sectionRoute(key: ProductSectionKey): string | undefined {
    const product = this.linkTarget();
    const section = PRODUCT_SECTIONS.find((candidate) => candidate.key === key);
    if (!product || !section || !product.sections.includes(key)) return undefined;
    const path = section.path ? `${product.route}/${section.path}` : product.route;

    return localePath(path, this.language.lang());
  }
}
